import { Client as TwilioConversationsClient } from '@twilio/conversations'
import { AxiosError } from 'axios'

import { isAndroid, isIOS } from 'src/utils/capacitor'

import { client } from './client'
import { ConnectionRequestResponseType } from './connect'
import * as ENDPOINTS from './endpoints'

export interface AccessTokenResponseType extends JSON {
  accessToken: string
}

const detectPlatform = (): 'apn' | 'fcm' | 'web' => {
  if (isIOS()) return 'apn'
  if (isAndroid()) return 'fcm'
  return 'web'
}

export const getConversationAccessToken = async (
  platform: 'apn' | 'fcm' | 'web' = detectPlatform()
): Promise<AccessTokenResponseType | ConnectionRequestResponseType> => {
  try {
    const response = await client.get<AccessTokenResponseType>(ENDPOINTS.GET_ACCESS_TOKEN, {
      platform,
    })
    return response.data
  } catch (error) {
    if ((error as AxiosError).response?.status === 400) {
      return { message: 'User info not found' }
    }
    return { message: 'Query Twilio Failed' }
  }
}

export const getTwilioConversationClient = (token: string) => {
  const test = new TwilioConversationsClient(token)
  return test
}

export const deleteConnection = async (
  connectionId: string
): Promise<ConnectionRequestResponseType> => {
  try {
    const response = await client.delete(`${ENDPOINTS.DELETE_CONNECTION}${connectionId}/`)
    return response.data as ConnectionRequestResponseType
  } catch (error) {
    if ((error as AxiosError).response?.status === 404) {
      return { message: 'Connection not found' }
    }
    if ((error as AxiosError).response?.status === 403) {
      return { message: 'You are not authorized to delete this connection' }
    }
    return { message: 'Failed to delete connection' }
  }
}

interface BlockUserRequest extends JSON {
  blockedUserId: string | number
}

export const blockUser = async (blockedUserId: string | number) => {
  const response = await client.post<unknown, BlockUserRequest>(ENDPOINTS.BLOCK_USER, {
    blockedUserId,
  } as BlockUserRequest)
  return response.data
}

export const leaveGroup = async (connectionId: string): Promise<ConnectionRequestResponseType> => {
  try {
    const response = await client.delete(`${ENDPOINTS.LEAVE_GROUP}${connectionId}/`)
    return response.data as ConnectionRequestResponseType
  } catch (error) {
    if ((error as AxiosError).response?.status === 404) {
      return { message: 'Connection not found' }
    }
    if ((error as AxiosError).response?.status === 403) {
      return { message: 'You are not part of this connection' }
    }
    return { message: 'Failed to leave group' }
  }
}

/**
 * Log a message to the backend for analytics tracking.
 * This is called after successfully sending a message via Twilio SDK.
 * The request is fire-and-forget - we don't block on the response.
 */
export interface LogMessageParams extends JSON {
  conversation_sid: string
  message_sid: string
  message_type?: 'text' | 'media'
}

export const logMessage = async (params: LogMessageParams): Promise<void> => {
  try {
    await client.post(ENDPOINTS.LOG_MESSAGE, params)
  } catch (error) {
    // Fire-and-forget: log error but don't throw
    // This prevents message logging failures from affecting the user experience
    console.warn('Failed to log message for analytics:', error)
  }
}
